import React, { useContext, useEffect, useState } from 'react';
import ProjectsTableDefault from '../default/table';
import Card from '../../../components/card';
import Loader from '../../../components/loader';
import { IoIosAdd } from 'react-icons/io';
import {
    ProjectsContext,
    PrivateProjectsContext,
} from '../../../context/ProjectsContext';
import { OrganisationsContext } from '../../../context/OrganisationsContext';
import { UserContext } from '../../../context/UserContext';
import { CustomersContext } from '../../../context/CustomersContext';
import {
    createCustomer,
    createHourBlock,
    createProject,
    deleteHourBlock,
    deleteProject,
    getCustomers,
    getPrivateProjects,
    getProjects,
    updateHourBlock,
    updateProject,
} from '../../../api/hours/HoursAPI';
import { useTranslation } from 'react-i18next';
import {
    getAdminPermission,
    getWritePermission,
} from '../../../helpers/getPermission';
import Button from '../../../components/shared/button';
import moment from 'moment';
import showNotification from '../../../helpers/showNotification';

function HoursPageProjects({ type }) {
    const { t } = useTranslation();

    const isPrivate = type === 'private';

    const { userState } = useContext(UserContext);

    const projects = isPrivate ? PrivateProjectsContext : ProjectsContext;
    const { projectsState, projectsDispatch } = useContext(projects);

    const { organisationsState } = useContext(OrganisationsContext);

    const { customersState, customersDispatch } = useContext(CustomersContext);

    const selectedOrgHash = organisationsState.data
        ? organisationsState.data.find((org) => org.active).org_hash
        : null;

    const demoOrgHash = process.env.REACT_APP_DEMO_ORG_HASH;
    const [userHash, setUserHash] = useState(
        selectedOrgHash === demoOrgHash
            ? 'URICHAR'
            : userState?.data?.user_hash || null
    );

    const [formattedProjects, setFormattedProjects] = useState([]);

    const [projectsErrors, setProjectsErrors] = useState(null);

    useEffect(() => {
        if (isPrivate) {
            if (selectedOrgHash === demoOrgHash) {
                setUserHash('URICHAR');
            } else {
                setUserHash(userState?.data?.user_hash || null);
            }
        }
        void fetchProjects();
    }, [selectedOrgHash]);

    useEffect(() => {
        setFormattedProjects(formatDataForTable(projectsState?.data));
    }, [customersState?.data, projectsState?.data]);

    useEffect(() => {
        const errorsObj = projectsState?.data?.reduce((acc, project) => {
            return { ...acc, [project.id]: null };
        }, {});
        setProjectsErrors(errorsObj);
    }, [projectsState?.data]);

    useEffect(() => {
        fetchCustomers();
    }, [selectedOrgHash]);

    async function fetchProjects() {
        projectsDispatch({ type: 'SET_LOADING' });
        try {
            const date = moment().day(0).format('YYYY-MM-DD');
            const res = isPrivate
                ? await getPrivateProjects(userHash, { params: { date } })
                : await getProjects({ params: { date } });
            if (res.status === 202 && isPrivate) {
                projectsDispatch({ type: 'SET_NO_LOADING' });
            } else if (res.status === 204) {
                projectsDispatch({ type: 'SET_DATA', payload: [] });
            } else {
                projectsDispatch({ type: 'SET_DATA', payload: res.data });
            }
        } catch (err) {
            projectsDispatch({ type: 'SET_ERROR', payload: err });
        }
    }

    async function fetchCustomers() {
        customersDispatch({ type: 'SET_LOADING' });
        try {
            const res = await getCustomers();
            if (res.status === 204) {
                customersDispatch({ type: 'SET_EMPTY_DATA' });
            } else {
                customersDispatch({ type: 'SET_DATA', payload: res.data });
            }
        } catch (err) {
            customersDispatch({ type: 'SET_ERROR', payload: err });
        }
    }

    function addRow() {
        projectsDispatch({
            type: 'SET_EMPTY_ROW',
            payload: { name: '', description: '', id: 'new' },
        });
    }

    async function handleCreateCustomer(name) {
        customersDispatch({ type: 'SET_LOADING' });
        try {
            const res = await createCustomer({ name });
            if (res.status === 200) {
                customersDispatch({
                    type: 'SET_DATA',
                    payload: [...customersState?.data, res?.data],
                });
            }
            return res?.data?.id;
        } catch (err) {
            customersDispatch({ type: 'SET_ERROR', payload: err });
            return null;
        }
    }

    async function handleCreateHourBlock(hours, monthly, customerId) {
        const formattedMonthly = Boolean(monthly);
        try {
            const res = await createHourBlock({
                hours: +hours,
                monthly: formattedMonthly,
                customer_id: customerId,
                sku: '',
            });

            if (res.status === 200) {
                const data = res?.data;
                return data?.id;
            }
        } catch (err) {
            return null;
        }
    }
    async function handleUpdateHourBlock(id, hours, monthly, customerId) {
        const formattedMonthly = Boolean(monthly);
        try {
            const res = await updateHourBlock({
                id: id,
                hours: +hours,
                monthly: formattedMonthly,
                customer_id: customerId,
                sku: '',
            });
            if (res.status === 200) {
                return res?.data?.id;
            }
        } catch (err) {
            return null;
        }
    }

    async function handleDeleteHourBlock(id) {
        try {
            await deleteHourBlock(id);
            return;
        } catch (err) {
            showNotification('Failed to delete an hours block');
            return;
        }
    }

    async function handleUpdateProjects(prevRowState, updatedRow) {
        projectsDispatch({ type: 'SET_LOADING' });
        const originalValues = prevRowState?.original;
        const updatedProject = updatedRow.values;
        const onlyOldProjects = projectsState?.data.filter(
            (project) => project.id && project?.id !== 'new'
        );

        let customerId = updatedProject?.customer?.value
            ? updatedProject?.customer?.value
            : originalValues?.customer
            ? originalValues?.customer
            : null;
        let hoursBlockId = originalValues?.hour_block_id
            ? originalValues?.hour_block_id
            : null;

        //check if project has a new customer
        if (
            updatedProject?.customer?.__isNew__ ||
            (customerId && !originalValues?.customer) ||
            (updatedProject?.customer?.length && !originalValues?.customer)
        ) {
            //this is a new customer and it has to be created first
            try {
                const res = await handleCreateCustomer(
                    updatedProject?.customer?.label
                        ? updatedProject?.customer?.label
                        : updatedProject?.customer
                );

                if (!res) {
                    projectsDispatch({
                        type: 'SET_ERROR',
                        payload: 'Failed to create a customer',
                    });
                    return;
                } else {
                    customerId = res;
                }
            } catch (err) {
                projectsDispatch({
                    type: 'SET_ERROR',
                    payload: err,
                });
                return;
            }
        }

        // check if project has new hours data
        // (there is no hours in the original row an there is hours data in the updated values
        if (!originalValues.hours && updatedProject?.hours) {
            // create new hour block, get its id
            try {
                const res = await handleCreateHourBlock(
                    updatedProject.hours,
                    updatedProject?.monthly,
                    customerId
                );

                if (!res) {
                    projectsDispatch({
                        type: 'SET_ERROR',
                        payload: 'Failed to create an hours block',
                    });
                    return;
                } else {
                    hoursBlockId = res;
                }
            } catch (err) {
                projectsDispatch({
                    type: 'SET_ERROR',
                    payload: err,
                });
                return;
            }
        }

        //if there is hour_block.id in the original row and the updated hours limit is 0 -> delete the hour block
        if (hoursBlockId && !updatedProject?.hours) {
            await handleDeleteHourBlock(hoursBlockId);
            hoursBlockId = null;
        }

        // if hours limit has been updated -> update hour block
        const hasHourBlockBeenUpdated =
            originalValues.hours &&
            updatedProject?.hours &&
            (originalValues.hours != updatedProject?.hours ||
                originalValues?.monthly != updatedProject?.monthly ||
                originalValues?.customer != customerId);

        if (hasHourBlockBeenUpdated) {
            try {
                const res = await handleUpdateHourBlock(
                    originalValues?.hour_block_id,
                    updatedProject.hours,
                    updatedProject?.monthly,
                    customerId
                );

                if (!res) {
                    projectsDispatch({
                        type: 'SET_ERROR',
                        payload: 'Failed to update an hours block',
                    });
                    return;
                }
            } catch (err) {
                projectsDispatch({
                    type: 'SET_ERROR',
                    payload: err,
                });
                return;
            }
        }

        // format data from the table for the API
        const formattedProjectData = hoursBlockId
            ? {
                  name: updatedProject?.name,
                  description: updatedProject?.description
                      ? updatedProject?.description
                      : '',
                  hour_block_id: hoursBlockId,
                  id: originalValues?.id,
              }
            : {
                  id: originalValues?.id,
                  name: updatedProject?.name,
                  description: updatedProject?.description
                      ? updatedProject?.description
                      : '',
              };

        const isUpdatedRow = onlyOldProjects.find(
            (project) => project.id === originalValues?.id
        );

        try {
            const res = isUpdatedRow
                ? isPrivate
                    ? await updateProject({
                          ...formattedProjectData,
                          user_hash: userHash,
                      })
                    : await updateProject(formattedProjectData)
                : isPrivate
                ? await createProject({
                      ...formattedProjectData,
                      user_hash: userHash,
                  })
                : await createProject(formattedProjectData);

            if (res.status === 202) {
                projectsDispatch({ type: 'SET_NO_LOADING' });
            } else if (res) {
                const data = res?.data;
                const updatedProjects = [
                    ...onlyOldProjects?.filter((p) => p?.id !== data?.id),
                ];
                projectsDispatch({
                    type: 'SET_DATA',
                    payload: [...updatedProjects, data],
                });
            }
        } catch (err) {
            setProjectsErrors({
                ...projectsErrors,
                [projectOriginalState?.id]: err?.response?.data
                    ? err?.response?.data
                    : 'Failed to delete the project',
            });
            projectsDispatch({ type: 'SET_NO_LOADING' });
            return 'error';
        }
    }

    async function handleDeleteProject(projectOriginalState) {
        projectsDispatch({ type: 'SET_LOADING' });

        //check if it is a new row
        if (projectOriginalState?.id === 'new') {
            projectsDispatch({ type: 'SET_DELETE_EMPTY_ROW' });
            return;
        }

        try {
            const res = await deleteProject(projectOriginalState?.id);
            if (res.status === 202 && isPrivate) {
                projectsDispatch({ type: 'SET_NO_LOADING' });
            } else if (res) {
                //if deleted successfully, delete related hour block
                if (projectOriginalState?.hour_block_id) {
                    await handleDeleteHourBlock(
                        projectOriginalState?.hour_block_id
                    );
                }

                const updatedProjects = projectsState?.data?.filter(
                    (p) => p?.id !== projectOriginalState?.id
                );
                projectsDispatch({
                    type: 'SET_DATA',
                    payload: updatedProjects,
                });
            }
        } catch (err) {
            setProjectsErrors({
                ...projectsErrors,
                [projectOriginalState?.id]: err?.response?.data
                    ? err?.response?.data
                    : 'Failed to delete the project',
            });
            projectsDispatch({ type: 'SET_NO_LOADING' });
            return 'error';
        }
    }

    function formatDataForTable(dataToFormat) {
        const formatted = dataToFormat?.map((project) => {
            const customer = project?.hour_block
                ? customersState?.data?.find(
                      (cust) => cust?.id === project?.hour_block?.customer_id
                  )
                : null;

            return {
                id: project?.id,
                name: project?.name,
                description: project?.description,
                available_hours: project?.available_hours,
                hour_block_id: project?.hour_block?.id,
                hours: project?.hour_block?.hours,
                monthly: project?.hour_block?.monthly,
                customer: customer?.id,
                error: null,
            };
        });

        return formatted;
    }

    function renderActionButtons() {
        if (
            getAdminPermission(organisationsState?.data) ||
            (isPrivate && getWritePermission(organisationsState?.data))
        ) {
            return (
                <Button
                    onClick={addRow}
                    extraClasses={'h-8'}
                    variant={'auth'}
                    title={isPrivate ? 'Add private project' : 'Add project'}
                    disabled={projectsState?.data?.find(
                        (item) => item.name === ''
                    )}
                >
                    <IoIosAdd size={20} /> {t('button.add')}
                </Button>
            );
        }
    }

    //dont render private projects if  it is read user
    if (isPrivate && !getWritePermission(organisationsState?.data)) {
        return <></>
    }
    if (projectsState?.loading)
        return (
            <Card
                title={
                    isPrivate
                        ? t('hours_page.private_projects.title')
                        : t('hours_page.organisation_projects.title')
                }
            >
                <div className="flex mt-8 justify-center">
                    <Loader color={'#C2C7D7'} size={35} speedMultiplier={0.8} />
                </div>
            </Card>
        );

    if (projectsState?.error)
        return (
            <Card
                title={
                    isPrivate
                        ? t('hours_page.private_projects.title')
                        : t('hours_page.organisation_projects.title')
                }
            >
                <p className="flex mt-8 justify-center text-center">
                    {projectsState?.error.response?.data ||
                        projectsState?.error.message ||
                        projectsState?.error ||
                        'Oops! Something went wrong!'}
                </p>
            </Card>
        );

    return (
        <Card
            title={
                isPrivate
                    ? t('hours_page.private_projects.title')
                    : t('hours_page.organisation_projects.title')
            }
            renderActionButtons={renderActionButtons}
        >
            {projectsState?.data ? (
                <ProjectsTableDefault
                    data={formattedProjects ? formattedProjects : []}
                    updateProjects={handleUpdateProjects}
                    deleteProject={handleDeleteProject}
                    type={type}
                    projectsErrors={projectsErrors}
                    setProjectsErrors={setProjectsErrors}
                    isFixed={false}
                />
            ) : null}
        </Card>
    );
}

export default HoursPageProjects;
